import type { Schemas } from '@argzombie/api-client';

/*
 * Stories & Tech Step 2 content types (chance #68, occupied #125, rescue #126, holdout #127, crafting #134, MacGuffin #130), from the
 * generated `@argzombie/api-client` schemas (S2-K regenerated them), plus the vocabularies of the shared-cs contracts files
 * (`ChanceDefs.cs`, `OccupiedDefs.cs`, `RescueDef.cs`, `HoldoutDefs.cs`, `TechDefs.cs`, `KeyPartDefs.cs`) in the C# order.
 */

export type ChanceEventsDef = Schemas['ChanceEventsDef'];
export type ChanceRulesDef = Schemas['ChanceRulesDef'];
export type ChanceEventDef = Schemas['ChanceEventDef'];
export type ChanceEligibilityDef = Schemas['ChanceEligibilityDef'];
export type ChanceTriggerDef = Schemas['ChanceTriggerDef'];
export type ChanceRoomRuleDef = Schemas['ChanceRoomRuleDef'];

export type OccupiedRulesDef = Schemas['OccupiedRulesDef'];
export type OccupiedTellRollDef = Schemas['OccupiedTellRollDef'];
export type CrewDef = Schemas['CrewDef'];
export type CrewMemberDef = Schemas['CrewMemberDef'];
export type HumanDef = Schemas['HumanDef'];
export type OccupiedTellDef = Schemas['OccupiedTellDef'];
/** disposition → approach → stance → weight. */
export type StanceGrid = Record<string, Record<string, Record<string, number>>>;

export type RescueRulesDef = Schemas['RescueRulesDef'];
/**
 * ADR 0064 (map menus): `autoSpawn` and `autoSpawnGapMinutes` are in shared-cs `RescueRadioDef` but not yet in the generated client
 * (mm-qa regenerates it on the integration branch); the intersection is harmless once they are.
 */
export type RescueRadioDef = Schemas['RescueRadioDef'] & { autoSpawn?: boolean; autoSpawnGapMinutes?: number };
export type RescueEscortDef = Schemas['RescueEscortDef'];
export type RescueScenarioDef = Schemas['RescueScenarioDef'];
export type RescueFreeDef = Schemas['RescueFreeDef'];
export type RescueWavesDef = Schemas['RescueWavesDef'];
export type RescueTrappedDef = Schemas['RescueTrappedDef'];
export type RescueBeatsDef = Schemas['RescueBeatsDef'];
export type RescueTwistDef = Schemas['RescueTwistDef'];

export type HoldoutDef = Schemas['HoldoutDef'];
export type HoldoutEntryDef = Schemas['HoldoutEntryDef'];
export type HoldoutWaveDef = Schemas['HoldoutWaveDef'];
export type HoldoutWaveMixDef = Schemas['HoldoutWaveMixDef'];
export type HoldoutCrateDef = Schemas['HoldoutCrateDef'];
export type HoldoutRulesDef = Schemas['HoldoutRulesDef'];
export type PlaceableDef = Schemas['PlaceableDef'];

export type TechTierDef = Schemas['TechTierDef'];
export type TechRequirementDef = Schemas['TechRequirementDef'];
export type ModulePowerDef = Schemas['ModulePowerDef'];
export type CraftingRulesDef = Schemas['CraftingRulesDef'];
export type BuildOrderLimitsDef = Schemas['BuildOrderLimitsDef'];
export type PowerRulesDef = Schemas['PowerRulesDef'];
export type WorkshopRoomDef = Schemas['WorkshopRoomDef'];
export type FieldKitDef = Schemas['FieldKitDef'];

export type SiteKindDef = Schemas['SiteKindDef'];
export type KeyPartDef = Schemas['KeyPartDef'];
export type KeyPartBeatDef = Schemas['KeyPartBeatDef'];
export type HaulDef = Schemas['HaulDef'];
export type KeyPartRulesDef = Schemas['KeyPartRulesDef'];
export type KeyPartHaulRulesDef = Schemas['KeyPartHaulRulesDef'];
export type HaulClassRuleDef = Schemas['HaulClassRuleDef'];
export type VirtualSiteDef = Schemas['VirtualSiteDef'];

/** `HumanRoles.All`. */
export const HUMAN_ROLES = ['resident', 'gunner', 'rusher', 'leader'] as const;

// ----- vocabularies (C# constants, same order) -----------------------------------------------------------------------------------

/** `ChanceTriggerKinds.All`. */
export const CHANCE_TRIGGER_KINDS = ['enter_room', 'first_runner_seen', 'pass_set_piece'] as const;
/** `OccupiedDispositions.All`. */
export const OCCUPIED_DISPOSITIONS = [
  'normal',
  'desperate',
  'hostile',
  'cannibal',
  'mad',
  'quiet',
] as const;
/** `OccupiedApproaches.All`. */
export const OCCUPIED_APPROACHES = ['sneak', 'announce', 'hot'] as const;
/** `OccupiedStances.All`. */
export const OCCUPIED_STANCES = [
  'welcome',
  'wary',
  'standoff',
  'caught_out',
  'firefight',
  'trap',
  'quiet',
] as const;
/** `OccupiedGates.All`. */
export const OCCUPIED_GATES = ['yard', 'defences', 'inside'] as const;
/** `PlaceableKinds.All`. */
export const PLACEABLE_KINDS = ['mine', 'barricade', 'trap', 'light'] as const;
/** The nav areas a placeable can mark (`holdoutRules.areaCost` keys). */
export const NAV_AREAS = ['barricade', 'wire', 'spikes'] as const;
/** `HaulClasses.All`. */
export const HAUL_CLASSES = ['pocket', 'heavy', 'two_person'] as const;
/** `RecipeWhere`: "" reads as home. */
export const RECIPE_WHERE = ['home', 'field'] as const;
/** Holdout entry kinds named on the page (content may add more). */
export const HOLDOUT_ENTRY_KINDS = ['rollup', 'door', 'chained', 'window'] as const;
/** Rescue free-objective kinds named on the page (content may add more). */
export const RESCUE_FREE_KINDS = ['cut', 'pry', 'dig', 'unbar'] as const;
/** Rescue trapped slots. */
export const RESCUE_SLOTS = ['main', 'second'] as const;
/** The Step 2 item categories (`ItemCategories`): the tech tree's, on top of today's. */
export const TECH_ITEM_CATEGORIES = [
  'raw',
  'intermediate',
  'part',
  'workshop_tool',
  'key_part',
] as const;

/**
 * The workshop room's fixed spots (crafting.html §4 "The workshop room"): 12 station spots, in the page's layout order. The tool wall
 * is the back wall (`crafting.room.toolWall`), not a slot.
 */
export const ROOM_SLOTS = [
  'front_1',
  'front_2',
  'front_3',
  'front_4',
  'front_5',
  'front_6',
  'back_1',
  'back_2',
  'back_3',
  'back_4',
  'back_5',
  'window',
] as const;
export type RoomSlot = (typeof ROOM_SLOTS)[number];
